<template>
  <DataTable
    :class="classes"
    data-id="sms-messages"
    :data-phone-id="selectedPhone || '0'"
    :fields="smsFields"
    :has-filter="false"
    :items="itemsSmss"
    :loading="loading"
    :no-card="noCard"
    :enable-delete-all="true"
    :page="page"
    :pageable="smss ? smss.pageable : null"
    :total-elements="smss ? smss.totalElements : null"
    :search-filter="searchSms || ''"
    :sort="sortSms"
    :icon="showPhoneSelector ? undefined : 'sms'"
    :title="'SMS/TXT Messages'"
    enable-date-picker
    :disable-new="true"
    :has-unread-only-toggle="true"
    :page-size="size"
    @edit="onEdit"
    @delete="onDelete"
    @delete-all="onDeleteAll"
    @new="onNew"
    @page="onPageSmss"
    @refresh="refreshSms"
    @sort="onSortSms"
    @date-range="onDateRange"
    @unread-only="onUnreadOnlyChange"
    @pagesize="onPageSize"
    @filter-change="onFilterChange"
  >
    <template #title>
      <PhoneSelect
        v-if="showPhoneSelector"
        size="sm"
        :defaultPhoneId="selectedPhone"
        @change="onPhoneChange"
        :allow-all-phones="true"
      />
      <span v-else>SMS/TXT messages</span>
    </template>
  </DataTable>
</template>
<script lang="ts" setup>
import type {
  GetSmsMessagesPaginatedRequest,
  PageSmsProjection,
  SmsProjection,
} from '@mailslurp/admin-sdk';
import { GetSmsMessagesPaginatedSortEnum } from '@mailslurp/admin-sdk';
import type { DataTableEditResult } from '../../lib/services/types';
import { loadFetch, SELECT_ALL_PHONES } from '../../lib/services/helpers';
import { pageSms } from '../../lib/services/fields';
import type { DateRange } from '../../lib/services/TimeService';
import { maxDate, minDate } from '../../lib/services/TimeService';
import { computed, onMounted, ref } from 'vue';

import { useStore3 } from '../../store/main';
import { storeToRefs } from 'pinia';
import { navigateTo } from '#imports';
import type { Sort } from '../../store/types';
import PhoneSelect from '../select/PhoneSelect.vue';
import DataTable from './parts/DataTable.vue';

const onFilterChange = async (search: string) => {
  page.value = 0;
  searchSms.value = search;
  await refreshSms();
};
const store3 = useStore3();
const index = ref(0);
const selectedPhone = ref();
const onPhoneChange = async (v: string | null) => {
  if (!v || v === SELECT_ALL_PHONES) {
    selectedPhone.value = null;
  } else {
    selectedPhone.value = v;
  }
  index.value = index.value + 1;
  await refreshSms();
};

const onPageSize = (p: number) => {
  size.value = p;
  page.value = 0;
  refreshSms();
};

const props = withDefaults(
  defineProps<{
    noCard?: boolean;
    phoneNumber?: string;
    classes?: string;
    showPhoneSelector?: boolean;
  }>(),
  {
    classes: '',
    noCard: false,
    phoneNumber: '',
    showPhoneSelector: false,
  },
);

const loading = ref<boolean>(false);
const page = ref(0);
const size = ref(10);
const sortSms = ref<GetSmsMessagesPaginatedSortEnum>(
  GetSmsMessagesPaginatedSortEnum.DESC,
);
const unreadOnly = ref<boolean>(false);
const searchSms = ref<string | null>(null);
const smsFields = ref(pageSms);

const itemsSmss = computed(() => {
  return smss.value?.content ?? [];
});
const { smsMessages } = storeToRefs(store3);

const smss = computed<PageSmsProjection | null>(() => {
  return smsMessages.value;
});

const onNew = async () => {
  await navigateTo(`/phone-numbers/create`);
};

const onEdit = async (payload: DataTableEditResult<SmsProjection>) => {
  await navigateTo(
    `/phone-numbers/${payload.item.phoneNumber}/sms/${payload.item.id}`,
  );
};

const onDeleteAll = async () => {
  await loadFetch(
    () => store3.deleteAllSms({ phoneNumberId: props.phoneNumber }),
    store3,
    loading,
  );
  await refreshSms();
};

const onDelete = async (payload: { id: string }) => {
  // TODO test on delete
  await loadFetch(
    () => store3.deleteSmsMessage({ smsId: payload.id }),
    store3,
    loading,
  );
  await refreshSms();
};

const onUnreadOnlyChange = async (newValue: boolean) => {
  unreadOnly.value = newValue;
  await refreshSms();
};

const since = ref<Date | null | undefined>(null);
const before = ref<Date | null | undefined>(null);

const onDateRange = (dateRange: DateRange | undefined) => {
  before.value = dateRange?.before ?? maxDate;
  since.value = dateRange?.since ?? minDate;
  refreshSms();
};

const refreshSms = async () => {
  const payload: GetSmsMessagesPaginatedRequest = {
    before: before.value?.toISOString() ?? undefined,
    since: since.value?.toISOString() ?? undefined,
    page: page.value,
    size: size.value,
    sort: sortSms.value,
    search: searchSms.value ?? undefined,
    phoneNumber: selectedPhone.value ?? props.phoneNumber,
    unreadOnly: unreadOnly.value,
  };
  const p = () => store3.getSmsMessages(payload);
  await loadFetch(p, store3, loading);
};

const onPageSmss = (index0based: number) => {
  page.value = index0based;
  refreshSms();
};

const onSortSms = (value: Sort) => {
  sortSms.value = value;
  refreshSms();
};

watch(
  () => props.phoneNumber,
  (v) => (selectedPhone.value = v),
);

onMounted(async () => {
  await refreshSms();
});
</script>
